import React, { useEffect, useState } from "react";
import { useFieldArray, useForm, useFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TriangleAlert } from "lucide-react";
import { ControlledUnifiedInput } from "../ui/customs/controlled-unified-input";
import { Button } from "../ui/button";
import { Alert, AlertDescription } from "../ui/alert";
import { BillingEntityForm } from "../shared/billingEntity/BillingEntityForm";
import {
  baseBillingEntitySchema,
  BaseBillingEntityFormData,
  DEFAULT_BILLING_ENTITY_VALUES,
} from "../../types/forms/shared/billingEntity.form";
import { RegisterFormData } from "../../types/forms/auth/registerFormData.form";
import { EntityCard } from "./EntityCard";

interface RegisterStepCompanyProps {
  onSubmit: () => void;
}

const RegisterStepCompany: React.FC<RegisterStepCompanyProps> = ({ onSubmit }) => {
  const [canSubmitBillingEntity, setCanSubmitBillingEntity] = useState(false);
  const [stateBillingEntityForm, setStateBillingEntityForm] = useState<{
    editIndex: number | undefined;
    show: boolean;
  }>({ editIndex: undefined, show: false });

  const { control, watch } = useFormContext<RegisterFormData>();
  const { fields, remove, update, append } = useFieldArray({
    control,
    name: "billingEntities" as const,
  });
  const billingEntities = watch("billingEntities");

  const billingEntityForm = useForm<BaseBillingEntityFormData>({
    resolver: zodResolver(baseBillingEntitySchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: DEFAULT_BILLING_ENTITY_VALUES,
  });

  useEffect(() => {
    if (fields.length === 0) {
      setStateBillingEntityForm({ editIndex: undefined, show: true });
    }
  }, [fields.length]);

  const handleSaveBillingEntity = async (data: BaseBillingEntityFormData) => {
    if (stateBillingEntityForm.editIndex !== undefined) {
      update(stateBillingEntityForm.editIndex, data);
    } else {
      append(data);
    }
    billingEntityForm.reset(DEFAULT_BILLING_ENTITY_VALUES);
    setStateBillingEntityForm({ editIndex: undefined, show: false });
    window.scrollTo(0, 0);
  };

  const handleCancelBillingEntity = () => {
    setStateBillingEntityForm({ editIndex: undefined, show: false });
    billingEntityForm.reset(DEFAULT_BILLING_ENTITY_VALUES);
  };

  const billingEntityFormContent = (
    <BillingEntityForm
      control={billingEntityForm.control}
      setValue={billingEntityForm.setValue}
      onCanSubmitChange={setCanSubmitBillingEntity}
    />
  );

  return (
    <div className="flex flex-col gap-8 py-28 md:px-20 lg:px-8">
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="font-jakarta-sans text-3xl font-semibold text-black">Créez votre compte</h1>
        <p className="text-base text-gray-600">Entrez vos informations pour créer votre espace.</p>
      </div>

      <div className="flex flex-col gap-4">
        <ControlledUnifiedInput label="Nom commercial" control={control} name="businessName" />

        <Alert className="border-yellow-500 bg-yellow-50">
          <TriangleAlert className="h-4 w-4 -translate-y-1" color="#F59E0B" />
          <AlertDescription className="text-yellow-800">
            Assurez-vous que les informations renseignées lors de l'inscription soient exactes.
          </AlertDescription>
        </Alert>

        <h2 className="text-base font-semibold text-blue-600">Entité de facturation par défaut</h2>

        {fields.map((_item, index) => {
          const isEditing =
            stateBillingEntityForm.show && stateBillingEntityForm.editIndex === index;
          return (
            <React.Fragment key={index}>
              <EntityCard
                isEditing={isEditing}
                onEdit={() => {
                  setStateBillingEntityForm({ editIndex: index, show: true });
                  billingEntityForm.reset(fields[index]);
                }}
                onSave={billingEntityForm.handleSubmit(handleSaveBillingEntity)}
                onCancel={handleCancelBillingEntity}
                canSave={canSubmitBillingEntity}
                onRemove={() => remove(index)}
                summary={
                  <>
                    <p>Entité de facturation {index === 0 ? "par défaut" : index + 1}</p>
                    <p>{fields[index]?.companyName}</p>
                  </>
                }
                form={billingEntityFormContent}
              />
              {index === 0 && !isEditing && (
                <h2 className="text-base font-semibold text-blue-600">
                  Entité de facturation supplémentaire
                </h2>
              )}
            </React.Fragment>
          );
        })}

        {stateBillingEntityForm.show && stateBillingEntityForm.editIndex === undefined && (
          <EntityCard
            isEditing
            summary={null}
            form={billingEntityFormContent}
            onSave={billingEntityForm.handleSubmit(handleSaveBillingEntity)}
            onCancel={fields.length > 0 ? handleCancelBillingEntity : undefined}
            canSave={canSubmitBillingEntity}
          />
        )}

        {!stateBillingEntityForm.show && (
          <Button
            onClick={() => {
              setStateBillingEntityForm({ editIndex: undefined, show: true });
              billingEntityForm.reset(DEFAULT_BILLING_ENTITY_VALUES);
            }}
            variant="tertiary"
            className="py-8 sm:py-2"
          >
            Ajouter une entité <br className="block sm:hidden" />
            supplémentaire (optionnel)
          </Button>
        )}
      </div>

      {billingEntities.length > 0 && !stateBillingEntityForm.show && (
        <Button onClick={onSubmit} className="self-end">
          Suivant
        </Button>
      )}
    </div>
  );
};

export default RegisterStepCompany;
